export type ContinuityRecordKind = "leadership" | "coaching";

export type CanonicalMomentSource =
  | "transcript_upload_processed"
  | "reflection_submitted"
  | "leader_synthesis_finalized"
  | "manual_reflection_submitted"
  | "transcript_review_completed"
  | "coaching_note_finalized"
  | "coaching_synthesis_approved"
  | "coaching_reflection_saved"
  | "context_graph_node_created"
  | "derived_theme_created"
  | "inferred_tension_created"
  | "memory_write_created"
  | "relationship_edge_created"
  | "runtime_artifact_created"
  | "hidden_runtime_structure"
  | string;

export interface CanonicalMomentEvent {
  id: string;
  source: CanonicalMomentSource;
  humanEventId?: string | null;
}

export interface ContinuityRecordSummary {
  label: "Leadership Record" | "Coaching Record";
  count: number;
  counterText: string;
  accumulationText: string;
}

const LEADERSHIP_MOMENT_SOURCES = new Set<CanonicalMomentSource>([
  "transcript_upload_processed",
  "reflection_submitted",
  "leader_synthesis_finalized",
  "manual_reflection_submitted",
]);

const COACHING_MOMENT_SOURCES = new Set<CanonicalMomentSource>([
  "transcript_review_completed",
  "coaching_note_finalized",
  "coaching_synthesis_approved",
  "coaching_reflection_saved",
]);

function isCanonicalSource(kind: ContinuityRecordKind, source: CanonicalMomentSource): boolean {
  return kind === "leadership"
    ? LEADERSHIP_MOMENT_SOURCES.has(source)
    : COACHING_MOMENT_SOURCES.has(source);
}

function pluralizeRecordUpdate(count: number, kind: ContinuityRecordKind): string {
  const noun = kind === "leadership" ? "leadership record update" : "coaching record update";
  return count === 1 ? noun : `${noun}s`;
}

export function countCanonicalMoments(params: {
  kind: ContinuityRecordKind;
  events: CanonicalMomentEvent[];
}): number {
  const countedHumanEvents = new Set<string>();

  for (const event of params.events) {
    if (!isCanonicalSource(params.kind, event.source)) continue;
    const key = event.humanEventId?.trim() || event.id.trim();
    if (!key) continue;
    countedHumanEvents.add(key);
  }

  return countedHumanEvents.size;
}

export function buildLeadershipRecordSummary(params: {
  events: CanonicalMomentEvent[];
}): ContinuityRecordSummary {
  const count = countCanonicalMoments({ kind: "leadership", events: params.events });
  return buildLeadershipRecordSummaryFromCount(count);
}

export function buildLeadershipRecordSummaryFromCount(count: number): ContinuityRecordSummary {
  return {
    label: "Leadership Record",
    count,
    counterText: `${count} ${pluralizeRecordUpdate(count, "leadership")}`,
    accumulationText: "Meaningful leadership record updates are accumulating over time.",
  };
}

export function buildCoachingRecordSummary(params: {
  events: CanonicalMomentEvent[];
  leaderName?: string | null;
}): ContinuityRecordSummary {
  const count = countCanonicalMoments({ kind: "coaching", events: params.events });
  return buildCoachingRecordSummaryFromCount(count, params.leaderName);
}

export function buildCoachingRecordSummaryFromCount(
  count: number,
  relationshipLeaderName?: string | null,
): ContinuityRecordSummary {
  const leaderName = relationshipLeaderName?.replace(/\s+/g, " ").trim();
  return {
    label: "Coaching Record",
    count,
    counterText: `${count} ${pluralizeRecordUpdate(count, "coaching")}${leaderName ? ` for ${leaderName}` : ""}`,
    accumulationText: "Meaningful coaching memory updates are accumulating over time.",
  };
}
